import React, { useState, useEffect } from 'react';
import { openViewerWithDocument, extractUrnFromTableRow, isDocumentRow, isDocumentViewable } from '../utilities/viewerOperations';
import { useAuth } from '../contexts/AuthContext';
import { useViewerSupport } from '../hooks/useViewerSupport';

const DocumentViewerButtonBar = ({
    setSelectedLeftUrn,
    setSelectedRightUrn,
    openViewerLeft,
    openViewerRight,
    selectedDocument
}) => {
    const { isAuthenticated, isFetchingToken, getAccessToken } = useAuth();
    const { checkSupport, loading: supportLoading } = useViewerSupport();
    const [isViewerSupported, setIsViewerSupported] = useState(null);

    // Check viewer support when selectedDocument changes
    useEffect(() => {
        const checkDocumentSupport = async () => {
            if (!selectedDocument || !isDocumentRow(selectedDocument)) {
                setIsViewerSupported(null);
                return;
            }

            // Get the file name from the selected document
            const fileName = selectedDocument.displayName || selectedDocument.name;
            console.log('Checking support for document:', fileName);
            
            if (!fileName) {
                setIsViewerSupported(false);
                return;
            }

            try {
                const isSupported = await checkSupport(fileName);
                setIsViewerSupported(isSupported);
            } catch (error) {
                console.error('Error checking document viewer support:', error);
                setIsViewerSupported(false);
            }
        };

        checkDocumentSupport();
    }, [selectedDocument, checkSupport]);
    const handleOpenInViewer = () => {
        const urn = extractUrnFromTableRow(selectedDocument);
        console.log('Setting left viewer URN:', urn);
        setSelectedLeftUrn(urn);
        // Open the viewer if it's not already opened
        if (openViewerLeft) {
            openViewerLeft();
        }
    };

    const handleOpenInViewerRight = () => {
        const urn = extractUrnFromTableRow(selectedDocument);
        console.log('Setting right viewer URN:', urn);
        setSelectedRightUrn(urn);
        // Open the viewer if it's not already opened
        if (openViewerRight) {
            openViewerRight();
        }
    };

    const handleOpenInACC = () => {
        if (!selectedDocument) {
            console.log('No document selected for ACC link');
            return;
        }

        // Use the webView URL directly from the document data
        const webViewUrl = selectedDocument.webView;

        if (!webViewUrl) {
            console.log('No webView URL found in document data');
            return;
        }

        window.open(webViewUrl, '_blank');
    };



    // Check if a document is selected
    const isDocumentSelected = () => {
        return isDocumentRow(selectedDocument);
    };

    // Check if the selected document is an Excel file
    const isExcelFile = () => {
        if (!selectedDocument) return false;
        
        const fileName = selectedDocument.displayName || selectedDocument.name || '';
        const extension = fileName.toLowerCase().split('.').pop();
        return extension === 'xls' || extension === 'xlsx';
    };

    // Enhanced logic that includes viewer support check
    const isViewerDisabled = !isAuthenticated || 
                             isFetchingToken || 
                             !isDocumentSelected() || 
                             supportLoading || 
                             (isViewerSupported !== null && isViewerSupported === false);
    
    // Debug logging
    console.log('DocumentViewerButtonBar Debug:', {
        isAuthenticated,
        isFetchingToken,
        isDocumentSelected: isDocumentSelected(),
        selectedDocument,
        supportLoading,
        isViewerSupported,
        isViewerDisabled
    });
    
    const isOpenInACCDisabled = !isDocumentSelected() || !selectedDocument?.webView;
    
    // Generate appropriate tooltip messages
    const getViewerTooltip = () => {
        if (!isAuthenticated) return "Please log in to use the viewer";
        if (isFetchingToken) return "Loading authentication...";
        if (!isDocumentSelected()) return "Please select a document to open in viewer";
        if (supportLoading) return "Checking if document is supported by viewer...";
        if (isViewerSupported === false) return "This document type is not supported by the viewer";
        return "Open selected document in viewer";
    };

    return (
        <div className="btn-toolbar" role="toolbar" aria-label="Document viewer toolbar">
            <div className="btn-group me-2" role="group" aria-label="Viewer buttons">
                <button
                    type="button"
                    className={`btn btn-sm ${isViewerDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                    onClick={handleOpenInViewer}
                    disabled={isViewerDisabled}
                    title={isViewerDisabled ? getViewerTooltip() : "Open selected document in left viewer"}
                >
                    <i className="bi bi-eye"></i> View Left
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${isViewerDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                    onClick={handleOpenInViewerRight}
                    disabled={isViewerDisabled}
                    title={isViewerDisabled ? getViewerTooltip() : "Open selected document in right viewer"}
                >
                    <i className="bi bi-eye"></i> View Right
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${isOpenInACCDisabled ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                    onClick={handleOpenInACC}
                    disabled={isOpenInACCDisabled}
                    title={isOpenInACCDisabled ?
                        "Please select a document to open in ACC" :
                        "Open selected document in ACC"
                    }
                >
                    <i className="bi bi-box-arrow-up-right"></i> Open in ACC
                </button>
            </div>
        </div>
    );
};

export default DocumentViewerButtonBar;